import React from 'react';
import { AlertTriangle, CheckCircle2, Cpu, MinusCircle } from 'lucide-react';
import { useTranslation } from '../i18n';

type JsonRecord = Record<string, unknown>;

export interface CodesysCapabilityView {
  ready: boolean;
  readyState: string;
  profileName?: string;
  ideVersion?: string;
  executableName?: string;
  deferredInstallationFileCount: number;
  supportedBackends: string[];
  remediation?: string;
}

function record(value: unknown): JsonRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonRecord
    : null;
}

export function readCodesysCapabilityView(value: unknown): CodesysCapabilityView | null {
  const root = record(value);
  if (!root) return null;
  const capability = record(root.codesys)
    ?? (root.schemaVersion === 'realplc.codesys.capabilities.v1' ? root : null);
  if (!capability) return null;
  const selectedIde = record(capability.selectedIde);
  const issues = Array.isArray(capability.preflightIssues)
    ? capability.preflightIssues.map(record).filter((item): item is JsonRecord => Boolean(item))
    : [];
  return {
    ready: capability.ready === true,
    readyState: typeof capability.readyState === 'string'
      ? capability.readyState
      : capability.ready === true ? 'ready' : 'not_ready',
    profileName: typeof selectedIde?.profileName === 'string'
      ? selectedIde.profileName
      : undefined,
    ideVersion: typeof selectedIde?.version === 'string'
      ? selectedIde.version
      : undefined,
    executableName: typeof selectedIde?.executablePath === 'string'
      ? selectedIde.executablePath.split(/[\\/]/).filter(Boolean).pop()
      : undefined,
    deferredInstallationFileCount: Number(selectedIde?.deferredInstallationFileCount ?? 0) || 0,
    supportedBackends: Array.isArray(capability.supportedBackends)
      ? capability.supportedBackends.filter((item): item is string => typeof item === 'string')
      : [],
    remediation: typeof issues[0]?.remediation === 'string'
      ? issues[0].remediation
      : undefined,
  };
}

export interface CodesysAgentCandidate {
  status?: string;
  projectId?: string | null;
  capabilitySnapshot?: JsonRecord | null;
  capabilities?: JsonRecord | null;
}

/**
 * Pick the Agent whose CODESYS state the UI should show, using the same rule as
 * the gateway: one organization can have several engineering PCs online whose
 * heartbeats land milliseconds apart, so "the first online agent" flips between
 * machines on every poll. Rank by how much of a CODESYS tool chain each one
 * reports and let the project binding only break ties — CODESYS validates
 * against the Agent's own Standard.project, so an organization-wide pairing is
 * usable too.
 */
export function pickCodesysAgent<T extends CodesysAgentCandidate>(
  agents: T[],
  projectId?: string | null,
): T | null {
  let best: T | null = null;
  let bestRank = 0;
  for (const agent of agents) {
    if (agent.status !== 'online') continue;
    const view = readCodesysCapabilityView(agent.capabilitySnapshot ?? agent.capabilities);
    const rank = (view?.ready ? 4 : view ? 2 : 0)
      + (projectId && agent.projectId === projectId ? 1 : 0);
    if (rank > bestRank) {
      best = agent;
      bestRank = rank;
    }
  }
  return best;
}

function stateText(state: string, zh: boolean): string {
  const labels: Record<string, [string, string]> = {
    ready: ['Ready for native validation', '已就绪，可执行原生验证'],
    deferred_installation_pending: [
      'CODESYS plug-in installation is incomplete',
      'CODESYS 插件安装尚未完成',
    ],
    scripting_not_installed: [
      'CODESYS Scripting is not installed',
      '尚未安装 CODESYS Scripting',
    ],
    ide_not_found: ['Supported CODESYS V3.5 SP15-SP22 was not found', '未找到支持的 CODESYS V3.5 SP15-SP22'],
    template_not_found: ['Standard.project was not found', '未找到 Standard.project'],
    node_not_found: ['Packaged Node.js runtime was not found', '未找到随包 Node.js 运行时'],
    worker_not_found: ['Independent CODESYS Worker was not found', '未找到独立 CODESYS Worker'],
  };
  const pair = labels[state] ?? ['Environment is not ready', '环境尚未就绪'];
  return zh ? pair[1] : pair[0];
}

export const CodesysCapabilityCard: React.FC<{ capabilities: unknown }> = ({ capabilities }) => {
  const { language } = useTranslation();
  const zh = language === 'Chinese';
  const view = readCodesysCapabilityView(capabilities);
  if (!view) return null;

  const message = stateText(view.readyState, zh);
  return (
    <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Cpu size={16} className="text-cyan-700 dark:text-cyan-300" />
            <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {zh ? 'CODESYS 原生闭环验证' : 'CODESYS native closed-loop validation'}
            </h4>
          </div>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            {zh
              ? '使用用户本机 IDE 和独立 ScriptEngine Worker；不依赖 MCP，不下载到真实 PLC。'
              : 'Uses the local IDE and an independent ScriptEngine Worker; no MCP or real PLC download.'}
          </p>
        </div>
        {view.ready
          ? <CheckCircle2 size={18} className="shrink-0 text-emerald-600" />
          : <AlertTriangle size={18} className="shrink-0 text-amber-600" />}
      </div>

      <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${view.ready
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
        : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'}`}>
        <div className="font-semibold">{message}</div>
        {view.profileName ? <div className="mt-1 text-xs opacity-80">{view.profileName}</div> : null}
        {view.readyState === 'deferred_installation_pending' ? (
          <div className="mt-1 text-xs leading-relaxed">
            {zh
              ? `检测到 ${view.deferredInstallationFileCount} 个待安装文件。请在 Connector 设置中运行修复，等待安装完成并关闭 IDE。`
              : `${view.deferredInstallationFileCount} deferred files remain. Run Repair from Connector settings, wait for installation, then close the IDE.`}
          </div>
        ) : null}
        {view.readyState === 'scripting_not_installed' ? (
          <div className="mt-1 text-xs leading-relaxed">
            {zh
              ? '请为当前选择的 SP15-SP22 IDE 安装官方免费 Scripting 组件后重试。'
              : 'Install the official no-extra-cost Scripting component for the selected SP15-SP22 IDE, then retry.'}
          </div>
        ) : null}
      </div>

      <div className="mt-3 grid gap-1 text-xs text-zinc-600 dark:text-zinc-300 sm:grid-cols-2">
        {[
          zh ? 'L1 原生编译 + Generate Code' : 'L1 native compile + Generate Code',
          zh ? 'L2 RealPLC 工控规则' : 'L2 RealPLC industrial rules',
          zh ? 'L3 IDE Simulation + 冻结 TestSpec' : 'L3 IDE Simulation + frozen TestSpec',
          zh ? '原 .project SHA-256 不变校验' : 'Original .project SHA-256 verification',
        ].map((label) => (
          <div key={label} className="flex items-center gap-2">
            {view.ready
              ? <CheckCircle2 size={13} className="text-emerald-600" />
              : <MinusCircle size={13} className="text-zinc-400" />}
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

CodesysCapabilityCard.displayName = 'CodesysCapabilityCard';
